"use client";

import { type MotionValue, useMotionValue } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { claimAudioFocus, releaseAudioFocus } from "@/lib/audio-focus";
import { safePreviewUrl } from "@/lib/music/links";

export type PreviewStatus = "idle" | "loading" | "playing" | "paused" | "error";

export interface PreviewTarget {
  /** Stable key for the song (profile_song id or catalog id). */
  key: string;
  previewUrl: string | null;
  /** Analytics only. */
  ownerUserId?: string;
  surface?: "profile" | "picker" | "chat" | "sheet" | "post" | "comment";
}

export interface MusicPreview {
  key: string | null;
  status: PreviewStatus;
  /** 0..1 through the clip; a motion value so the ring never re-renders React. */
  progress: MotionValue<number>;
  play: (target: PreviewTarget) => void;
  pause: () => void;
  toggle: (target: PreviewTarget) => void;
}

let ownerSeq = 0;

/**
 * One <audio> per owner, created on the first play — inside the tap that
 * asked for it, which is what iOS needs to allow sound. Once an element has
 * played from a gesture, later `play()` calls on it (auto-advance to the next
 * song) are allowed too, so the element is reused rather than replaced.
 *
 * Playback stops when the owner unmounts — including when Next hides the
 * route in an <Activity> on navigation, which runs effect cleanups — and when
 * the page is backgrounded. Music never follows you off a profile.
 */
export function useMusicPreview({
  onEnded,
}: {
  onEnded?: (key: string) => void;
} = {}): MusicPreview {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [status, setStatus] = useState<PreviewStatus>("idle");
  const progress = useMotionValue(0);
  const keyRef = useRef<string | null>(null);
  const frameRef = useRef<number | null>(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const stopTicker = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  const startTicker = useCallback(() => {
    stopTicker();
    const tick = () => {
      const audio = audioRef.current;
      if (audio && audio.duration > 0) {
        progress.set(Math.min(1, audio.currentTime / audio.duration));
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
  }, [progress, stopTicker]);

  // One audio-focus holder per hook instance (src/lib/audio-focus.ts): a
  // preview starting anywhere — or a video's sound being turned on — pauses
  // whatever had the speaker.
  const owner = useRef<{ id: string; pause: () => void } | null>(null);
  owner.current ??= { id: `music-preview-${++ownerSeq}`, pause: () => {} };
  const focusId = owner.current.id;

  const pause = useCallback(() => {
    audioRef.current?.pause();
    stopTicker();
    setStatus((current) =>
      current === "playing" || current === "loading" ? "paused" : current,
    );
    releaseAudioFocus(focusId);
  }, [focusId, stopTicker]);
  owner.current.pause = pause;

  const ensureAudio = useCallback(() => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.preload = "none";
    audio.addEventListener("playing", () => {
      setStatus("playing");
      startTicker();
    });
    audio.addEventListener("waiting", () => setStatus("loading"));
    audio.addEventListener("pause", () => {
      stopTicker();
      setStatus((current) => (current === "playing" ? "paused" : current));
    });
    audio.addEventListener("ended", () => {
      stopTicker();
      progress.set(1);
      setStatus("paused");
      releaseAudioFocus(focusId);
      const ended = keyRef.current;
      if (ended) onEndedRef.current?.(ended);
    });
    audio.addEventListener("error", () => {
      stopTicker();
      setStatus("error");
    });
    audioRef.current = audio;
    return audio;
  }, [focusId, progress, startTicker, stopTicker]);

  const play = useCallback(
    (target: PreviewTarget) => {
      const url = safePreviewUrl(target.previewUrl);
      if (!url) {
        pause();
        setKey(target.key);
        keyRef.current = target.key;
        setStatus("error");
        return;
      }
      claimAudioFocus({ id: focusId, release: () => owner.current?.pause() });

      const audio = ensureAudio();
      const sameClip = keyRef.current === target.key && audio.src === url;
      if (!sameClip) {
        audio.src = url;
        progress.set(0);
      } else if (audio.ended) {
        audio.currentTime = 0;
        progress.set(0);
      }
      keyRef.current = target.key;
      setKey(target.key);
      setStatus("loading");
      audio.play().catch(() => {
        // Autoplay policy or a dead URL: show it as stopped, not spinning.
        setStatus((current) => (current === "loading" ? "error" : current));
      });
      if (!sameClip) {
        captureClientEvent(ANALYTICS_EVENTS.PROFILE_MUSIC_PREVIEW_PLAYED, {
          surface: target.surface ?? "profile",
          owner_user_id: target.ownerUserId,
        });
      }
    },
    [ensureAudio, focusId, pause, progress],
  );

  const toggle = useCallback(
    (target: PreviewTarget) => {
      const playingThis =
        keyRef.current === target.key &&
        (status === "playing" || status === "loading");
      if (playingThis) pause();
      else play(target);
    },
    [pause, play, status],
  );

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") owner.current?.pause();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onHidden);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onHidden);
      owner.current?.pause();
      stopTicker();
      const audio = audioRef.current;
      if (audio) {
        audio.removeAttribute("src");
        audio.load();
      }
      audioRef.current = null;
    };
  }, [stopTicker]);

  return { key, status, progress, play, pause, toggle };
}
